import { Box, SelectChangeEvent, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import CropIcon from "../../../../assets/Fibo/icons/CropIcon";
import BriaDropDown, { DropDownItem } from "../../../../components/common/BriaDropDown/BriaDropDown";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import { AspectRatio } from "../../../../components/common/DropDowns/StaticDropdown";
import useStaticDropdown from "../../../../components/common/DropDowns/useStaticDropdown";
import styles from "./FiboAspectRatioButton.module.scss";

export interface IFiboAspectRatioButtonProps {
	onChange: (value: AspectRatio) => void;
	value: AspectRatio;
}

const FiboAspectRatioButton = ({ onChange, value }: IFiboAspectRatioButtonProps) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.settings.aspectRatio" });
	const { items } = useStaticDropdown({ tKeyPrefix: "staticDropdowns.aspectRatio" });

	const customSelectedKey = (item: DropDownItem<AspectRatio>, icon?: string | ReactNode) => (
		<Box className={styles.aspectRatioItem}>
			<Box className={styles.aspectRatioValueContainer}>
				<Box className={styles.aspectRatioIconContainer}>
					{icon ? (
						typeof icon === "string" ? (
							<img src={icon} alt={String(item.value)} />
						) : (
							icon
						)
					) : (
						<Box
							sx={{ aspectRatio: String(item.value).replace(":", "/") }}
							className={styles.aspectRatioIcon}
						/>
					)}
				</Box>
				<Typography className={styles.aspectRatioValue}>{String(item.value)}</Typography>
			</Box>
		</Box>
	);

	const customItems = items.reduce<DropDownItem<AspectRatio>[]>((acc, item) => {
		if (item.value === "original") return acc;
		acc.push({
			...item,
			customItem: customSelectedKey(item as DropDownItem<AspectRatio>),
			customSelectedKey: customSelectedKey(item as DropDownItem<AspectRatio>, <CropIcon />),
		} as DropDownItem<AspectRatio>);
		return acc;
	}, []);

	// TextToImageStore change handler (fibo)
	const handleAspectRatioChange = (e: SelectChangeEvent<AspectRatio>) => {
		const newValue = e.target.value as AspectRatio;
		onChange(newValue);
	};

	return (
		<BriaTooltip title={t("tooltip")}>
			<BriaDropDown<AspectRatio>
				items={customItems}
				value={value}
				onChange={handleAspectRatioChange}
				placeholder={t("placeholder")}
				hideArrowIcon
				componentsProps={{
					root: { className: styles.controlButton },
				}}
			/>
		</BriaTooltip>
	);
};

export default observer(FiboAspectRatioButton);
